import { useEffect, useSyncExternalStore } from "react";
import { readString, writeString } from "./storage";

export type ThemeChoice = "system" | "light" | "dark";

const STORAGE_KEY = "theme";
const VALID: ThemeChoice[] = ["system", "light", "dark"];

function getStored(): ThemeChoice {
  const v = readString(STORAGE_KEY, "system");
  return VALID.includes(v as ThemeChoice) ? (v as ThemeChoice) : "system";
}

function resolveTheme(choice: ThemeChoice): "light" | "dark" {
  if (choice === "light" || choice === "dark") return choice;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: "light" | "dark") {
  document.documentElement.setAttribute("data-theme", resolved);
}

let listeners: Array<() => void> = [];
let currentChoice: ThemeChoice = getStored();
let currentResolved: "light" | "dark" = resolveTheme(currentChoice);

applyTheme(currentResolved);

function notify() {
  for (const fn of listeners) fn();
}

if (typeof window !== "undefined") {
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (currentChoice !== "system") return;
    currentResolved = resolveTheme("system");
    applyTheme(currentResolved);
    notify();
  });
}

function subscribe(fn: () => void) {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter((l) => l !== fn);
  };
}

function getSnapshot() {
  return currentChoice;
}

export function useTheme() {
  const choice = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    currentResolved = resolveTheme(currentChoice);
    applyTheme(currentResolved);
  }, []);

  function setTheme(next: ThemeChoice) {
    currentChoice = next;
    currentResolved = resolveTheme(next);
    writeString(STORAGE_KEY, next);
    applyTheme(currentResolved);
    notify();
  }

  return { theme: choice, resolved: currentResolved, setTheme };
}
